// src/components/Realtime/VideoRoom.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";

type Role = "student" | "doctor" | "pt" | "parent" | "teacher";

type Props = {
  roomId: string;
  role: Role;
  onHangup: () => void;
  ws?: WebSocket | null;
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com";

const STUN_SERVERS: RTCConfiguration = {
  iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
};

function safeJsonParse(s: string) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function clean(v: any) {
  return String(v ?? "").trim();
}

function isPtRole(role: Role) {
  return role === "pt" || role === "parent" || role === "teacher";
}

function getAuthToken(role: Role): string {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem(role === "doctor" ? "doctor_token" : "") ||
    localStorage.getItem(role === "doctor" ? "doctor_access_token" : "") ||
    localStorage.getItem(role === "student" ? "student_token" : "") ||
    localStorage.getItem(role === "student" ? "student_access_token" : "") ||
    localStorage.getItem(isPtRole(role) ? "pt_token" : "") ||
    localStorage.getItem(isPtRole(role) ? "pt_access_token" : "") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem(role === "doctor" ? "doctor_token" : "") ||
    sessionStorage.getItem(role === "doctor" ? "doctor_access_token" : "") ||
    sessionStorage.getItem(role === "student" ? "student_token" : "") ||
    sessionStorage.getItem(role === "student" ? "student_access_token" : "") ||
    sessionStorage.getItem(isPtRole(role) ? "pt_token" : "") ||
    sessionStorage.getItem(isPtRole(role) ? "pt_access_token" : "") ||
    ""
  );
}

function buildWsUrl(apiBase: string, token: string, role: Role) {
  const base = (apiBase || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");
  const wsBase = base.startsWith("https://") ? base.replace(/^https:/, "wss:") : base.replace(/^http:/, "ws:");
  const qs = new URLSearchParams();
  if (token) qs.set("token", token);
  qs.set("role", role);

  if (isPtRole(role)) {
    const ptPk = clean(localStorage.getItem("pt_pk")) || clean(sessionStorage.getItem("pt_pk"));
    const ptCode = clean(localStorage.getItem("pt_code")) || clean(sessionStorage.getItem("pt_code"));
    const orgCode = clean(localStorage.getItem("org_code")) || clean(sessionStorage.getItem("org_code"));
    const displayName =
      clean(localStorage.getItem("display_name")) || clean(sessionStorage.getItem("display_name"));

    if (ptPk) qs.set("pt_pk", ptPk);
    if (ptCode) qs.set("pt_code", ptCode);
    if (orgCode) qs.set("org_code", orgCode);
    if (displayName) qs.set("display_name", displayName);
  }

  return `${wsBase}/realtime/ws?${qs.toString()}`;
}

const VideoRoom: React.FC<Props> = ({ roomId, role, onHangup, ws }) => {
  const [status, setStatus] = useState<"idle" | "connecting" | "connected" | "error">("idle");
  const [error, setError] = useState("");

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);

  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);

  const joinedRef = useRef(false);

  const internalWsRef = useRef<WebSocket | null>(null);
  const ownsWsRef = useRef(false);
  const [activeWs, setActiveWs] = useState<WebSocket | null>(null);

  const offerRetryTimerRef = useRef<number | null>(null);
  const offerAttemptsRef = useRef(0);
  const answerSeenRef = useRef(false);
  const pendingIceRef = useRef<RTCIceCandidateInit[]>([]);

  const clearOfferRetry = useCallback(() => {
    if (offerRetryTimerRef.current) {
      window.clearInterval(offerRetryTimerRef.current);
      offerRetryTimerRef.current = null;
    }
    offerAttemptsRef.current = 0;
  }, []);

  const cleanupPeer = useCallback(() => {
    clearOfferRetry();
    answerSeenRef.current = false;
    pendingIceRef.current = [];

    try {
      pcRef.current?.close();
    } catch {}

    pcRef.current = null;

    try {
      localStreamRef.current?.getTracks()?.forEach((t) => t.stop());
    } catch {}

    localStreamRef.current = null;

    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;

    joinedRef.current = false;
  }, [clearOfferRetry]);

  const send = useCallback(
    (msg: any) => {
      const w = activeWs;
      if (!w || w.readyState !== WebSocket.OPEN) return;
      try {
        w.send(JSON.stringify(msg));
      } catch {}
    },
    [activeWs]
  );

  const ensureJoined = useCallback(() => {
    if (joinedRef.current) return;
    send({ type: "chat.join", room_id: roomId, payload: { room_id: roomId } });
    joinedRef.current = true;
  }, [roomId, send]);

  const ensurePc = useCallback(() => {
    if (pcRef.current) return pcRef.current;

    const pc = new RTCPeerConnection(STUN_SERVERS);
    pcRef.current = pc;

    pc.ontrack = (ev) => {
      const stream = ev.streams?.[0];
      if (stream && remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
      }
    };

    pc.onicecandidate = (ev) => {
      if (!ev.candidate) return;
      send({
        type: "rtc.ice",
        room_id: roomId,
        candidate: ev.candidate,
        payload: { room_id: roomId, candidate: ev.candidate },
      });
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") {
        setStatus("connected");
        setError("");
        clearOfferRetry();
      }
    };

    return pc;
  }, [clearOfferRetry, roomId, send]);

  const startLocal = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;

    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    localStreamRef.current = stream;

    if (localVideoRef.current) localVideoRef.current.srcObject = stream;

    const pc = ensurePc();
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    return stream;
  }, [ensurePc]);

  const sendOffer = useCallback(
    async (iceRestart = false) => {
      const pc = ensurePc();

      if (pc.currentRemoteDescription || answerSeenRef.current) return;

      if (pc.signalingState === "have-local-offer") {
        try {
          await pc.setLocalDescription({ type: "rollback" } as any);
        } catch {}
      }

      const offer = await pc.createOffer(iceRestart ? ({ iceRestart: true } as any) : undefined);
      await pc.setLocalDescription(offer);

      send({
        type: "rtc.offer",
        room_id: roomId,
        sdp: offer,
        payload: { room_id: roomId, sdp: offer },
      });
    },
    [ensurePc, roomId, send]
  );

  const startCallAsCaller = useCallback(async () => {
    await startLocal();
    await sendOffer(false);

    clearOfferRetry();
    offerAttemptsRef.current = 0;

    offerRetryTimerRef.current = window.setInterval(async () => {
      const pc = pcRef.current;
      if (!pc) return;

      if (pc.currentRemoteDescription || answerSeenRef.current || pc.connectionState === "connected") {
        clearOfferRetry();
        return;
      }

      offerAttemptsRef.current += 1;
      if (offerAttemptsRef.current > 5) {
        clearOfferRetry();
        return;
      }

      try {
        await sendOffer(true);
      } catch {}
    }, 1500);
  }, [clearOfferRetry, sendOffer, startLocal]);

  const handleOfferAsReceiver = useCallback(
    async (sdp: any) => {
      const pc = ensurePc();
      await startLocal();

      if (pc.signalingState === "have-local-offer") {
        try {
          await pc.setLocalDescription({ type: "rollback" } as any);
        } catch {}
      }

      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      for (const queued of pendingIceRef.current.splice(0)) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(queued));
        } catch {}
      }
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      send({
        type: "rtc.answer",
        room_id: roomId,
        sdp: answer,
        payload: { room_id: roomId, sdp: answer },
      });
    },
    [ensurePc, roomId, send, startLocal]
  );

  const handleAnswerAsCaller = useCallback(
    async (sdp: any) => {
      const pc = ensurePc();
      answerSeenRef.current = true;
      clearOfferRetry();
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      for (const queued of pendingIceRef.current.splice(0)) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(queued));
        } catch {}
      }
    },
    [clearOfferRetry, ensurePc]
  );

  const handleIce = useCallback(async (candidate: any) => {
    if (!candidate) return;
    const pc = pcRef.current;

    // ICE can arrive before the offer/answer has installed a remote
    // description. Queue it instead of silently discarding it.
    if (!pc || pc.connectionState === "closed" || !pc.remoteDescription) {
      pendingIceRef.current.push(candidate);
      return;
    }

    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch {}
  }, []);

  useEffect(() => {
    if (ws) {
      ownsWsRef.current = false;
      setActiveWs(ws);
      return;
    }

    const token = getAuthToken(role);
    if (!token) {
      setStatus("error");
      setError("Missing auth token for video signaling. Please log in again.");
      setActiveWs(null);
      return;
    }

    const url = buildWsUrl(API_BASE, token, role);
    const w = new WebSocket(url);
    internalWsRef.current = w;
    ownsWsRef.current = true;
    setActiveWs(w);

    return () => {
      try {
        if (internalWsRef.current && internalWsRef.current.readyState === WebSocket.OPEN) {
          internalWsRef.current.close();
        }
      } catch {}
      internalWsRef.current = null;
      ownsWsRef.current = false;
    };
  }, [ws, role]);

  useEffect(() => {
    setError("");
    setStatus("connecting");
    joinedRef.current = false;
    answerSeenRef.current = false;
    clearOfferRetry();

    const w = activeWs;
    if (!w) {
      setStatus("error");
      setError("No WebSocket available for video signaling.");
      return;
    }

    const onOpen = () => {
      setStatus("connecting");
      setError("");

      try {
        const payload: any = {
          role,
          display_name:
            clean(localStorage.getItem("display_name")) || clean(sessionStorage.getItem("display_name")),
        };

        if (role === "student") {
          payload.student_id =
            clean(localStorage.getItem("student_id")) || clean(sessionStorage.getItem("student_id"));
          payload.org_code =
            clean(localStorage.getItem("org_code")) || clean(sessionStorage.getItem("org_code"));
        } else if (role === "doctor") {
          payload.doctor_id =
            clean(localStorage.getItem("doctor_id")) || clean(sessionStorage.getItem("doctor_id"));
        } else {
          payload.pt_pk = clean(localStorage.getItem("pt_pk")) || clean(sessionStorage.getItem("pt_pk"));
          payload.pt_code = clean(localStorage.getItem("pt_code")) || clean(sessionStorage.getItem("pt_code"));
          payload.org_code =
            clean(localStorage.getItem("org_code")) || clean(sessionStorage.getItem("org_code"));
          payload.pt_role =
            clean(localStorage.getItem("pt_role")) || clean(sessionStorage.getItem("pt_role")) || role;
        }

        w.send(JSON.stringify({ type: "_join", role, payload }));
      } catch {}

      ensureJoined();

      if (role !== "doctor") {
        startCallAsCaller().catch((e) => {
          setStatus("error");
          setError(e?.message || "Failed to start call.");
        });
      }
    };

    const onMessage = (ev: MessageEvent) => {
      const data = safeJsonParse(String(ev.data));
      if (!data) return;

      const type = String(data?.type || "");
      const payload = data?.payload ?? data;

      const rid = String(payload?.room_id || payload?.roomId || payload?.room || "");
      if (rid !== roomId) return;

      if (type === "rtc.offer" && role === "doctor") {
        handleOfferAsReceiver(payload?.sdp).catch(() => {});
        return;
      }

      if (type === "rtc.offer" && isPtRole(role)) {
        handleOfferAsReceiver(payload?.sdp).catch(() => {});
        return;
      }

      if (type === "rtc.answer" && role !== "doctor") {
        handleAnswerAsCaller(payload?.sdp).catch(() => {});
        return;
      }

      if (type === "rtc.ice") {
        handleIce(payload?.candidate).catch(() => {});
        return;
      }

      if (type === "rtc.hangup") {
        cleanupPeer();
        onHangup();
      }
    };

    const onCloseOrError = () => {
      setStatus("error");
      setError("WebSocket closed/error during video signaling.");
      clearOfferRetry();
    };

    w.addEventListener("open", onOpen);
    w.addEventListener("message", onMessage);
    w.addEventListener("close", onCloseOrError);
    w.addEventListener("error", onCloseOrError);

    if (w.readyState === WebSocket.OPEN) onOpen();

    return () => {
      w.removeEventListener("open", onOpen);
      w.removeEventListener("message", onMessage);
      w.removeEventListener("close", onCloseOrError);
      w.removeEventListener("error", onCloseOrError);
      cleanupPeer();
    };
  }, [
    activeWs,
    roomId,
    role,
    onHangup,
    clearOfferRetry,
    cleanupPeer,
    ensureJoined,
    handleAnswerAsCaller,
    handleOfferAsReceiver,
    handleIce,
    startCallAsCaller,
  ]);

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
        <div style={{ fontWeight: 1000 }}>
          🎥 Video Call —{" "}
          {status === "connected"
            ? "Connected ✅"
            : status === "connecting"
              ? "Connecting…"
              : status === "error"
                ? "Error"
                : "Idle"}
        </div>

        <button
          type="button"
          className="btn-danger"
          onClick={() => {
            try {
              send({ type: "rtc.hangup", room_id: roomId, payload: { room_id: roomId } });
            } catch {}

            cleanupPeer();

            if (ownsWsRef.current && internalWsRef.current) {
              try {
                internalWsRef.current.close();
              } catch {}
              internalWsRef.current = null;
            }

            onHangup();
          }}
        >
          End Call
        </button>
      </div>

      {error ? (
        <div className="notice error" style={{ marginTop: 10 }}>
          {String(error)}
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        <div
          style={{
            borderRadius: 14,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.12)",
            background: "#000",
          }}
        >
          <div style={{ padding: 8, color: "#fff", fontWeight: 900, fontSize: 12, opacity: 0.9 }}>You</div>
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={{ width: "100%", height: 280, objectFit: "cover", display: "block" }}
          />
        </div>

        <div
          style={{
            borderRadius: 14,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.12)",
            background: "#000",
          }}
        >
          <div style={{ padding: 8, color: "#fff", fontWeight: 900, fontSize: 12, opacity: 0.9 }}>Peer</div>
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            style={{ width: "100%", height: 280, objectFit: "cover", display: "block" }}
          />
        </div>
      </div>
    </div>
  );
};

export default VideoRoom;